import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { useRouterState } from '@tanstack/react-router'

import { whatsAppCtaProps } from '../lib/analytics'
import { FEMA_CATEGORY, FEMA_MESSAGE, FEMA_PATH } from '../data/fema'
import { STOCKING_APPLE_CATEGORY, STOCKING_APPLE_LEAD_ITEM, STOCKING_APPLE_MESSAGE, STOCKING_APPLE_PATH, STOCKING_APPLE_PRODUCT_NAME } from '../data/stockingAppleCream'

const WA_NUMBER = '6285704011028'
const DEFAULT_MESSAGE = 'Halo Dropify! Saya tertarik kulakan grosir. Bisa minta info harga dan stok?'

// Static pages whose topic is known up front. Dynamic routes (kategori, merek,
// produk, LP) are handled by slug in getFloatingWaMessage below.
const STATIC_MESSAGES: Record<string, string> = {
  [FEMA_PATH]: FEMA_MESSAGE,
  [STOCKING_APPLE_PATH]: STOCKING_APPLE_MESSAGE,
  '/katalog':
    'Halo Dropify! Saya lagi lihat katalog Dropify. Bisa minta info harga grosir dan cara ordernya?',
  '/pusat-grosir':
    'Halo Dropify! Saya tertarik kulakan dari pusat grosir Dropify. Bisa minta info harga dan minimum ordernya?',
  '/minimum-order':
    'Halo Dropify! Saya mau tanya soal minimum order. Bisa dijelaskan syarat dan harga grosirnya?',
  '/jaminan-ekspedisi':
    'Halo Dropify, saya ingin memakai ekspedisi pilihan untuk memeriksa pesanan sebelum transfer. Produk: ___; jumlah: ___; kota tujuan: ___; ekspedisi dan cabang: ___. Bisa jelaskan alurnya?',
  '/cara-jadi-reseller':
    'Halo Dropify! Saya tertarik jadi reseller Dropify. Bisa minta info cara mulai dan harga resellernya?',
  '/cari-importir':
    'Halo Dropify, saya sedang mencari barang impor untuk kulakan. Mohon kirim kategori produk yang tersedia dan cara meminta quotation.',
  '/bra-oreo':
    'Halo Dropify! Saya mau tanya bra Oreo untuk stok toko. Bisa kirim pilihan nomor model, stok, ukuran, warna, dan harga grosirnya?',
  '/sarung-tangan-proyek':
    'Halo Dropify! Saya mau tanya harga grosir sarung tangan proyek. Bisa minta katalog dan info stoknya?',
  '/sarung-tangan-pabrik':
    'Halo Dropify! Saya mau tanya harga grosir sarung tangan untuk pabrik. Bisa minta katalog dan info stoknya?',
  '/sarung-tangan-gekko':
    'Halo Dropify, saya ingin menanyakan sarung tangan Gekko Grip. Kondisi kerja: ___. Jumlah: ___. Mohon periksa seri Nitrile atau PU yang sesuai beserta stoknya.',
  '/sarung-tangan-comet':
    'Halo Dropify, saya ingin menanyakan sarung tangan COMET. Risiko kerja: ___. Jumlah: ___. Mohon periksa seri dan stok yang sesuai.',
  '/masker-comet':
    'Halo Dropify! Saya mau tanya masker COMET Theo. Bisa bantu bandingkan tipe, isi box, stok, dan harganya?',
  '/sarung-tangan-diamond':
    'Halo Dropify! Saya mau tanya sarung tangan Diamond. Bisa kirim foto stok aktual dan harga grosirnya?',
  '/sarung-tangan-bulldozer':
    'Halo Dropify! Saya mau tanya sarung tangan Bulldozer. Bisa kirim foto seri, spesifikasi, stok, dan harganya?',
  '/sarung-tangan-matahari':
    'Halo Dropify! Saya mau tanya sarung tangan Matahari. Bisa kirim pilihan varian, stok, dan harga grosirnya?',
  '/sarung-tangan-paskibra':
    'Halo Dropify! Saya mau tanya harga grosir sarung tangan paskibra. Bisa minta katalog dan info stoknya?',
  '/sarung-tangan-nitrile':
    'Halo Dropify! Saya mau tanya sarung tangan nitrile. Bisa bantu arahkan yang sekali pakai atau yang kerja, lalu cek stoknya?',
  '/supplier-apd-pabrik':
    'Halo Dropify! Saya sedang cari supplier APD untuk pabrik. Bisa minta katalog dan harga grosirnya?',
  '/supplier-bra-lingerie-surabaya':
    'Halo Dropify, saya mencari supplier lingerie dan bra grosir untuk toko. Produk yang dicari: ___. Kebutuhan: ___. Kota tujuan: ___. Mohon katalog dan quotation.',
  '/grosir-perlengkapan-haji-umroh':
    'Halo Dropify! Saya mau tanya harga grosir perlengkapan haji dan umroh. Bisa minta katalog dan info stoknya?',
  '/perlengkapan-haji-umroh':
    'Halo Dropify! Saya mau tanya harga grosir perlengkapan haji dan umroh. Bisa minta katalog dan info stoknya?',
  '/lp/manshet-sarung-tangan':
    'Halo Dropify! Saya mau tanya harga grosir manset sarung tangan. Bisa minta katalog dan info stoknya?',
  '/ebook-bikin-toko-online':
    'Halo Dropify! Saya habis baca soal ebook bikin toko online. Saya juga tertarik kulakan grosir, bisa minta info harganya?',
  '/search':
    'Halo Dropify, saya sedang mencari produk tertentu. Saya akan kirim nama produk, jumlah, dan kota tujuan untuk dicek admin.',
}

const CATEGORY_LABELS: Record<string, string> = {
  'bra-lingerie': 'Bra & Lingerie',
  'cd-underwear': 'CD & Underwear',
  'kaos-kaki': 'Kaos Kaki',
  'perlengkapan-medis': 'Perlengkapan Medis',
  masker: 'Masker',
  'piyama-oneset': 'Piyama & Oneset',
}

function humanizeSlug(slug: string) {
  return decodeURIComponent(slug).replace(/-/g, ' ').trim()
}

export function getFloatingWaMessage(pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/'

  const staticMessage = STATIC_MESSAGES[path]
  if (staticMessage) return staticMessage

  const kategori = path.match(/^\/kategori\/([^/]+)$/)
  if (kategori)
    return `Halo Dropify! Saya mau tanya harga grosir ${humanizeSlug(kategori[1])}. Bisa minta katalog lengkap dan info stoknya?`

  const merek = path.match(/^\/merek\/([^/]+)$/)
  if (merek)
    return `Halo Dropify! Saya tertarik produk merek ${humanizeSlug(merek[1])}. Bisa minta info harga grosir dan stoknya?`

  const produk = path.match(/^\/(?:produk|product)\/([^/]+)$/)
  if (produk)
    return `Halo Dropify! Saya mau tanya harga grosir produk ${humanizeSlug(produk[1])}. Bisa minta info stok dan minimum ordernya?`

  // Matches both the /lp/grosir-<slug> LPs and top-level moves like /grosir-kaos-kaki.
  const lpGrosir = path.match(/^(?:\/lp)?\/grosir-([^/]+)$/)
  if (lpGrosir)
    return `Halo Dropify! Saya mau tanya harga grosir ${humanizeSlug(lpGrosir[1])}. Bisa minta katalog dan info stoknya?`

  if (path === '/blog' || path.startsWith('/blog/'))
    return 'Halo Dropify! Saya habis baca artikel di blog Dropify dan tertarik kulakan grosir. Bisa minta info harga dan stoknya?'

  return DEFAULT_MESSAGE
}

export function getFloatingWaCategory(pathname: string): string | undefined {
  const path = pathname.replace(/\/+$/, '') || '/'
  if (path === FEMA_PATH) return FEMA_CATEGORY
  if (path === STOCKING_APPLE_PATH) return STOCKING_APPLE_CATEGORY
  if (path.startsWith('/produk/')) return 'Produk'
  if (path === '/supplier-bra-lingerie-surabaya') return 'Bra & Lingerie'
  if (path.includes('sarung-tangan')) return 'Sarung Tangan'
  if (path.includes('masker')) return 'Masker'
  const kategori = path.match(/^\/kategori\/([^/]+)$/)
  return kategori ? (CATEGORY_LABELS[kategori[1]] ?? humanizeSlug(kategori[1])) : undefined
}

/**
 * Site-wide floating WhatsApp CTA. Mounted once at the app root so it shows on
 * every page. The prefilled message follows the current route so the chat opens
 * with the product family the visitor was looking at.
 */
export function FloatingWhatsApp() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const message = getFloatingWaMessage(pathname)
  const lpCategory = getFloatingWaCategory(pathname)
  // The full-screen catalog has product-specific actions and a TOC contact
  // button. A second fixed action covers its product-detail link on phones.
  if (pathname.replace(/\/+$/, '') === '/katalog') return null
  return (
    <a
      href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('sitewide', {
        cta_placement: 'sticky_floating',
        lp_category: lpCategory,
        ...(pathname.replace(/\/+$/, '') === STOCKING_APPLE_PATH ? {
          lead_item_name: STOCKING_APPLE_PRODUCT_NAME,
          items: [STOCKING_APPLE_LEAD_ITEM],
        } : {}),
      })}
      className="floating-whatsapp-cta fixed bottom-6 right-6 z-50 flex items-center gap-2.5 bg-green-700 hover:bg-green-800 text-white px-4 py-3 rounded-full shadow-lg transition-colors"
      aria-label="Tanyakan harga grosir via WhatsApp"
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5 flex-shrink-0" />
      <span className="floating-whatsapp-label text-sm font-bold">Tanya Harga Grosir</span>
    </a>
  )
}
